import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postEvent } from '../api/postEvent';
import { Alert } from '../components/Alert/Alert';
import { BackBox } from '../components/BackBox/BackBox';
import { Header } from '../components/Header/Header';
import { StoreBadge } from '../components/StoreBadge/StoreBadge';
import {
  getImprintLink,
  getLegalLink,
  getPrivacyLink,
  links,
  routes
} from '../constants/constants';
import { copyToClipboard } from '../helper/copyToClipboard';
import { getDeviceType } from '../helper/device';
import { isDeviceIncompatible } from '../helper/isDeviceIncompatible';
import { useDeepStartLink } from '../hooks/useDeepStartLink';
import { changeShowStartButton, useStateValue } from '../hooks/useState';
import { useLocation } from '../react-router-hooks';
import styles from '../components/Header/Header.module.css';
import { StoreBadgeMobileNew } from 'src/components/StoreBadge/StoreBadgeMobileNew';
import { useGetInstalledRelatedApps } from 'src/hooks/useGetInstalledRelatedApps';
import { LanguageSelector } from 'src/components/languageSelector/languageSelector';
import { supportedLanguages } from '../helper/supportedLangs';
import { extractUrlParam } from 'src/helper/extractUrlParam';

let hidden: any;
let visibilityChange: any;

if (typeof document.hidden !== 'undefined') {
  // Opera 12.10 and Firefox 18 and later support
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
} else if (typeof (document as any).msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
} else if (typeof (document as any).webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}

export const HomeScreenMobileDelivery = () => {
  const { t } = useTranslation();
  const [
    { responseURI, uuid, caseType, caseStatus, coBranding, eIDMode },
    dispatch
  ] = useStateValue();
  const { navigate, location } = useLocation();
  const [type] = useState(getDeviceType());
  const { clipboardData } = useDeepStartLink({
    uuid,
    type,
    responseURI,
    isMobile: true
  });
  const deviceIncompatibility = isDeviceIncompatible({
    userAgent: navigator.userAgent
  });

  if (deviceIncompatibility) {
    // navigate(routes.INCOMPATIBILITY);

    postEvent({
      uuid,
      event: 'WEB.JUMP.DEVICEINCOMPATIBILITY'
    });
  }

  const copyMagic = useCallback(
    e => {
      if ((document as any)[hidden]) {
        copyToClipboard(clipboardData, uuid);

        postEvent({
          uuid,
          event: 'WEB.JUMP.COPYCLIPBOARD',
          details: {
            clipboardData
          }
        });
      }
    },
    [clipboardData]
  );

  useEffect(() => {
    document.addEventListener(visibilityChange, copyMagic, false);

    return () =>
      document.removeEventListener(visibilityChange, copyMagic, false);
  }, [copyMagic]);

  const [src, setSrc] = useState(
    `${process.env.REACT_APP_CMS}/assets/default-jump.png`
  );

  useEffect(() => {
    if (caseStatus && caseStatus.publicSPID && coBranding && coBranding.logo) {
      const url = coBranding.logo;

      fetch(url)
        .then(() => {
          setSrc(url);
        })
        .catch(error => console.warn(error));
    } else {
      setSrc('');
    }
  }, [caseStatus, coBranding]);

  const installedRelatedApps = useGetInstalledRelatedApps();

  const [{ showStartButton, deliveryPreview }] = useStateValue();

  useEffect(() => {
    const referral = extractUrlParam({
      url: location.search,
      type: 'referral'
    });

    if (referral === 'doublejump') {
      dispatch(changeShowStartButton({ showStartButton: true }));
    }
  }, [location]);

  const shouldShowStartButton =
    (showStartButton && getDeviceType() === 'ios') ||
    installedRelatedApps?.length;

  return (
    <div style={{ height: '100%' }}>
      <div
        id="main"
        className="main"
        style={{ paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}
      >
        <div
          style={{
            display: 'flex',
            width: '100%'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {/* {eIDMode && eIDMode !== '' && (
              <div
                className="newPageMessageBanner newPageMessageBanner-blue"
                style={{
                  position: 'initial',
                  transform: 'none',
                  marginBottom: 16
                }}
                dangerouslySetInnerHTML={{
                  __html: t(
                    '<b>Today, the eID service will be updated.</b><br /> <br />This means that the online ID function of your ID card (eID) only works to a limited extent. We apologize for any inconvenience.'
                  )
                }}
              />
            )} */}
            <div style={{ display: 'flex', alignItems: 'center', height: 97 }}>
              <img
                className={styles.iconMobile}
                style={{ height: 43, width: 37, paddingTop: 0 }}
                src={require('../assets/nect-logo-black.svg')}
              />

              {src && (
                <>
                  <img
                    src={require('../assets/close-cross.png')}
                    style={{ height: 30, marginLeft: 8, marginRight: 8 }}
                  />
                  <img src={src} style={{ height: 40 }} />
                </>
              )}
            </div>
            <div className="envelopeMobileWrapper">
              <img
                src={require('../assets/envelope_mobile.png')}
                alt="Envelope"
                style={{ objectFit: 'contain' }}
              />
            </div>
            <h1 className="titleMobileDelivery">
              {t('You received a letter from {companyName}').replace(
                '{companyName}',
                coBranding?.name ? coBranding.name : t('Nect')
              )}
            </h1>

            <p
              style={{
                color: '#6B7280',
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                maxWidth: 400,
                minHeight: 52,
                margin: '0 0 24px 0',
                padding: '0 16px'
              }}
              dangerouslySetInnerHTML={{
                __html: shouldShowStartButton
                  ? t(
                      'In order to open your letter you need to identify as <b style="color: #111928">{userName}</b>. For that we use the Nect Wallet App.'
                    ).replace(
                      '{userName}',
                      `${deliveryPreview?.poi_given_name} ${deliveryPreview?.poi_last_name_short}`
                    )
                  : t(
                      'In order to open your letter you need to identify with the Nect Wallet. Please download and continue the process as <b style="color: #111928">{userName}</b>.'
                    ).replace(
                      '{userName}',
                      `${deliveryPreview?.poi_given_name} ${deliveryPreview?.poi_last_name_short}`
                    )
              }}
            />

            <div
              style={{
                maxWidth: '100%',
                width: '90%',
                padding: '0 16px',
                paddingBottom: 25,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <StoreBadgeMobileNew
                ownButton
                target="_self"
                type={type}
                onBackground="white"
              />
            </div>
          </div>
        </div>
      </div>
      <footer
        style={{
          background: '#FAFBFC',
          padding: '40px 24px',
          marginTop: 100
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            fontSize: 15,
            lineHeight: '22px'
          }}
        >
          <div
            style={{ width: '50%', flexDirection: 'column', display: 'flex' }}
          >
            <span
              style={{
                color: '#0A0D1B',
                fontFamily: 'Poppins',
                fontWeight: 600,
                marginBottom: 16
              }}
            >
              {t('Help Centre')}
            </span>
            <a
              style={{
                marginBottom: 12,
                color: '#4B4F55'
              }}
              href={links.contact + '?uuid=' + uuid}
              onClick={() => {
                postEvent({
                  uuid,
                  event: 'WEB.JUMP.ACTION.MOBILE.MENU.CONTACT',
                  details: {}
                });
              }}
            >
              {t('Help Form')}
            </a>
            <a
              style={{
                color: '#4B4F55'
              }}
              href={links.faq + '?uuid=' + uuid}
              onClick={() => {
                postEvent({
                  uuid,
                  event: 'WEB.JUMP.ACTION.MOBILE.MENU.FAQ',
                  details: {}
                });
              }}
            >
              {t('FAQ')}
            </a>
          </div>
          <div
            style={{ width: '50%', flexDirection: 'column', display: 'flex' }}
          >
            <span
              style={{
                color: '#0A0D1B',
                fontFamily: 'Poppins',
                fontWeight: 600,
                marginBottom: 16
              }}
            >
              {t('Legal')}
            </span>
            <a
              style={{
                color: '#4B4F55',
                marginBottom: 12
              }}
              href={getImprintLink()}
              onClick={() => {
                postEvent({
                  uuid,
                  event: 'WEB.JUMP.ACTION.MOBILE.MENU.IMPRINT',
                  details: {}
                });
              }}
            >
              {t('Imprint')}
            </a>
            <a
              style={{
                color: '#4B4F55',
                marginBottom: 12
              }}
              href={getPrivacyLink()}
              onClick={() => {
                postEvent({
                  uuid,
                  event: 'WEB.JUMP.ACTION.MOBILE.MENU.DATA_PRIVACY',
                  details: {}
                });
              }}
            >
              {t('Privacy Policy')}
            </a>
            <a
              style={{
                color: '#4B4F55'
              }}
              href={getLegalLink()}
              onClick={() => {
                postEvent({
                  uuid,
                  event: 'WEB.JUMP.ACTION.MOBILE.MENU.LEGAL_INFO',
                  details: {}
                });
              }}
            >
              {t('Legal Information')}
            </a>
          </div>
        </div>
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <LanguageSelector langs={supportedLanguages} />
        </div>
        <div
          style={{
            width: '100%',
            color: '#797C81',
            textAlign: 'center',
            marginTop: 24
          }}
        >
          {`© Nect GmbH ${new Date().getFullYear()}`}
        </div>
      </footer>
    </div>
  );
};
